import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import { type MetricTrendPoint } from "@vega-api/generated/types/metricTrendPoint";
import {
  ChartContainer,
  ChartTooltip,
} from "@vega-core/components/shadcn/chart";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { ChartNoData } from "../../components/ChartNoData/ChartNoData";
import { ChartValueTooltip } from "../../components/ChartValueTooltip/ChartValueTooltip";
import {
  filterByTimeRange,
  formatMetricValue,
  formatPeriodLabel,
  getMetricLabel,
  type MetricKey,
  TimeRange,
} from "../../utils/formatFinancialReturns";

export { TimeRange };

type ChartPoint = {
  timestamp: number;
  value: number;
};

const CHART_COLOR = "var(--data-1)";
const CHART_HEIGHT_CLASS_NAME = "h-56 w-full";

const CHART_CONFIG = {
  value: {
    color: CHART_COLOR,
  },
};

const TIME_RANGE_BUTTONS: { value: TimeRange; label: string }[] = [
  { value: TimeRange.All, label: m.coInvestments_report_trend_all() },
  { value: TimeRange.OneYear, label: m.coInvestments_report_trend_1y() },
];

function hasMetricValue(
  point: MetricTrendPoint,
): point is MetricTrendPoint & { value: number } {
  return point.value != null;
}

function toChartPoints(points: MetricTrendPoint[]): ChartPoint[] {
  return points.filter(hasMetricValue).map((point) => ({
    timestamp: new Date(point.periodDate).getTime(),
    value: point.value,
  }));
}

type Props = {
  activeMetric: MetricKey;
  points: MetricTrendPoint[];
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  isLoading?: boolean;
  dataTestId?: string;
};

function isChartPoint(point: unknown): point is ChartPoint {
  // recharts types payload as broad unknown — narrow it explicitly
  if (typeof point !== "object" || point === null) return false;

  return (
    "timestamp" in point &&
    "value" in point &&
    typeof point.timestamp === "number" &&
    typeof point.value === "number"
  );
}

function TimeRangeControls({
  timeRange,
  onTimeRangeChange,
}: {
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {TIME_RANGE_BUTTONS.map(({ value, label }) => (
        <button
          aria-pressed={timeRange === value}
          className={cnTwMerge(
            "px-3 py-1 rounded-md body-small-medium focus-outline-themed",
            timeRange === value
              ? "bg-background/component text-text/primary"
              : "text-text/secondary hover:text-text/primary",
          )}
          data-testid={`time-range-button-${value}`}
          key={value}
          onClick={() => onTimeRangeChange(value)}
          type="button"
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function TrendTooltip({
  active,
  payload,
  activeMetric,
}: {
  active?: boolean;
  activeMetric: MetricKey;
  payload?: { payload?: unknown }[];
}) {
  if (!active || !payload?.length) return null;

  const point = payload[0].payload;

  if (!isChartPoint(point)) return null;

  return (
    <ChartValueTooltip
      label={formatPeriodLabel(point.timestamp)}
      value={formatMetricValue({ key: activeMetric, value: point.value })}
    />
  );
}

function TrendChartBody({
  activeMetric,
  chartData,
  isLoading,
}: {
  activeMetric: MetricKey;
  chartData: ChartPoint[];
  isLoading: boolean;
}) {
  if (isLoading) return <Skeleton className={CHART_HEIGHT_CLASS_NAME} />;

  if (chartData.length === 0) {
    return (
      <ChartNoData
        className={CHART_HEIGHT_CLASS_NAME}
        dataTestId="trend-chart-no-data"
        message={m.coInvestments_report_trend_noData()}
      />
    );
  }

  return (
    <ChartContainer className={CHART_HEIGHT_CLASS_NAME} config={CHART_CONFIG}>
      <LineChart
        data={chartData}
        margin={{ top: 8, right: 8, bottom: 0, left: 8 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          strokeOpacity={0.5}
          vertical={false}
        />
        <XAxis
          axisLine={false}
          dataKey="timestamp"
          domain={["dataMin", "dataMax"]}
          scale="time"
          tickFormatter={formatPeriodLabel}
          tickLine={false}
          tickMargin={8}
          type="number"
        />
        <YAxis
          axisLine={false}
          tickFormatter={(value: number) =>
            formatMetricValue({ key: activeMetric, value })
          }
          tickLine={false}
          tickMargin={8}
          width={48}
        />
        <ChartTooltip
          content={(tooltipProps) => (
            <TrendTooltip
              active={tooltipProps.active}
              activeMetric={activeMetric}
              payload={tooltipProps.payload}
            />
          )}
        />
        <Line
          activeDot={{ fill: CHART_COLOR, r: 6, strokeWidth: 0 }}
          dataKey="value"
          dot={{ fill: CHART_COLOR, r: 4, strokeWidth: 0 }}
          stroke={CHART_COLOR}
          strokeWidth={2}
          type="monotone"
        />
      </LineChart>
    </ChartContainer>
  );
}

export function FinancialReturnsTrendChart({
  activeMetric,
  points,
  timeRange,
  onTimeRangeChange,
  isLoading = false,
  dataTestId,
}: Props) {
  const chartData = toChartPoints(filterByTimeRange(points, timeRange));

  return (
    <div
      className="border border-border/primary rounded-lg p-6"
      data-testid={dataTestId}
    >
      <div className="flex items-center justify-between mb-4">
        <p className="body-base-semibold">
          {m.coInvestments_report_trend_title({
            metric: getMetricLabel(activeMetric),
          })}
        </p>
        <TimeRangeControls
          onTimeRangeChange={onTimeRangeChange}
          timeRange={timeRange}
        />
      </div>

      <TrendChartBody
        activeMetric={activeMetric}
        chartData={chartData}
        isLoading={isLoading}
      />
    </div>
  );
}
